// Frontmatter + small helpers for the Vault markdown surfaces.

/**
 * Parse leading YAML frontmatter (--- ... ---). Returns { data, body, hasFrontmatter }.
 * Supports simple `key: value` lines and inline arrays `key: [a, b, c]`.
 */
export function parseFrontmatter(md) {
  const text = md || "";
  const m = /^---\n([\s\S]*?)\n---[ \t]*(?:\n|$)/.exec(text);
  if (!m) return { data: {}, body: text, hasFrontmatter: false };
  const body = text.slice(m[0].length);
  const data = {};
  for (const raw of m[1].split("\n")) {
    const line = raw.trimEnd();
    const mm = /^([A-Za-z0-9_][A-Za-z0-9_\- ]*):[ \t]*(.*)$/.exec(line);
    if (!mm) continue;
    const key = mm[1].trim();
    let val = mm[2].trim();
    if (val.startsWith("[") && val.endsWith("]")) {
      val = val
        .slice(1, -1)
        .split(",")
        .map((s) => s.trim().replace(/^['"]|['"]$/g, ""))
        .filter(Boolean);
    } else {
      val = val.replace(/^['"]|['"]$/g, "");
    }
    data[key] = val;
  }
  return { data, body, hasFrontmatter: true };
}

/**
 * Approximate pixel coordinates of the caret (or any index) inside a textarea,
 * using a hidden mirror element. Used to anchor the [[wiki-link]] autocomplete.
 */
export function getCaretCoordinates(el, position) {
  const style = window.getComputedStyle(el);
  const div = document.createElement("div");
  const copy = [
    "boxSizing", "width", "paddingTop", "paddingRight", "paddingBottom",
    "paddingLeft", "borderTopWidth", "borderRightWidth", "borderBottomWidth",
    "borderLeftWidth", "fontFamily", "fontSize", "fontWeight", "fontStyle",
    "lineHeight", "letterSpacing", "textTransform",
  ];
  div.style.position = "absolute";
  div.style.visibility = "hidden";
  div.style.whiteSpace = "pre-wrap";
  div.style.wordWrap = "break-word";
  div.style.overflow = "hidden";
  copy.forEach((p) => { div.style[p] = style[p]; });

  div.textContent = el.value.substring(0, position);
  const span = document.createElement("span");
  span.textContent = el.value.substring(position) || ".";
  div.appendChild(span);
  document.body.appendChild(div);
  const coords = { top: span.offsetTop, left: span.offsetLeft };
  document.body.removeChild(div);
  return coords;
}

function escapeRe(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Strip leading frontmatter and ```action blocks for clean text scanning. */
export function stripMeta(md) {
  return (md || "").replace(/^---\n[\s\S]*?\n---\n?/, "").replace(/```action[\s\S]*?```/g, "");
}

/** All [[wiki-link]] / ![[embed]] targets (names only, alias/heading removed). */
export function extractWikiLinks(md) {
  const out = [];
  const re = /!?\[\[([^\]|\n#]+)(?:[#|][^\]\n]*)?\]\]/g;
  let m;
  while ((m = re.exec(md || "")) !== null) {
    const t = m[1].trim().replace(/\.md$/i, "");
    if (t) out.push(t);
  }
  return out;
}

/** A short, single-line context snippet around the first match of `name`. */
export function contextSnippet(md, name, radius = 48) {
  const text = stripMeta(md).replace(/\r/g, "");
  const re = new RegExp(escapeRe(name), "i");
  const m = re.exec(text);
  if (!m) return "";
  const i = m.index;
  const s = Math.max(0, i - radius);
  const e = Math.min(text.length, i + name.length + radius);
  const snip = text.slice(s, e).replace(/\n+/g, " ").replace(/\[\[|\]\]/g, "").trim();
  return (s > 0 ? "… " : "") + snip + (e < text.length ? " …" : "");
}

/**
 * First plain-text occurrence of `name` that is NOT already inside a [[wiki-link]].
 * Returns { index, length, snippet } against the original markdown, or null.
 */
export function findMention(md, name) {
  const text = md || "";
  if (!name) return null;
  const spans = [];
  const wl = /!?\[\[[^\]]*\]\]/g;
  let w;
  while ((w = wl.exec(text)) !== null) spans.push([w.index, w.index + w[0].length]);
  const re = new RegExp(`\\b${escapeRe(name)}\\b`, "gi");
  let m;
  while ((m = re.exec(text)) !== null) {
    const i = m.index;
    if (spans.some(([s, e]) => i >= s && i < e)) continue;
    const s = Math.max(0, i - 48);
    const e = Math.min(text.length, i + m[0].length + 48);
    const snippet = (s > 0 ? "… " : "") + text.slice(s, e).replace(/\n+/g, " ").trim() + (e < text.length ? " …" : "");
    return { index: i, length: m[0].length, snippet };
  }
  return null;
}

/** Wrap the span [index, index+length) in [[ ]] and return the new markdown. */
export function linkAtIndex(md, index, length) {
  return md.slice(0, index) + "[[" + md.slice(index, index + length) + "]]" + md.slice(index + length);
}

/**
 * Wrap EVERY plain-text occurrence of `name` (not already inside a [[link]])
 * in [[ ]]. Returns { markdown, count }.
 */
export function linkAllMentions(md, name) {
  const text = md || "";
  if (!name) return { markdown: text, count: 0 };
  const spans = [];
  const wl = /!?\[\[[^\]]*\]\]/g;
  let w;
  while ((w = wl.exec(text)) !== null) spans.push([w.index, w.index + w[0].length]);
  const re = new RegExp(`\\b${escapeRe(name)}\\b`, "gi");
  let result = "";
  let last = 0;
  let count = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    const i = m.index;
    if (spans.some(([s, e]) => i >= s && i < e)) continue;
    result += text.slice(last, i) + "[[" + m[0] + "]]";
    last = i + m[0].length;
    count++;
  }
  result += text.slice(last);
  return { markdown: result, count };
}

/** Full-text search across docs: returns [{ doc, snippet }] ranked by name-hit first. */
export function searchDocs(docs, query, limit = 60) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return [];
  const hits = [];
  for (const d of docs) {
    const name = (d.name || "").toLowerCase();
    const body = stripMeta(d.markdown).toLowerCase();
    const inName = name.includes(q);
    const bi = body.indexOf(q);
    if (!inName && bi < 0) continue;
    let snippet = "";
    if (bi >= 0) {
      const raw = stripMeta(d.markdown).replace(/\r/g, "");
      const s = Math.max(0, bi - 50);
      const e = Math.min(raw.length, bi + q.length + 60);
      snippet = (s > 0 ? "… " : "") + raw.slice(s, e).replace(/\n+/g, " ").trim() + (e < raw.length ? " …" : "");
    }
    hits.push({ doc: d, snippet, score: inName ? 0 : 1 });
  }
  hits.sort((a, b) => a.score - b.score || a.doc.name.localeCompare(b.doc.name));
  return hits.slice(0, limit);
}
